// The report builder's pure state (Talk BI spec §7.4, Task 5.1): wells, their limits, and the report panel a
// state makes (app/bi/definition.py). No DOM: bi-builder.jsx draws. A field is {name, kind: dim|measure|time, agg};
// the default date's year/quarter/month stand as dimensions named by the grain word (the server's GRAINS).
import {GRAINS} from './bi-tables.mjs';
import {qty} from './bi-format.mjs';
import {fieldTitle, nameOf} from './bi-report.mjs';

export const VISUALS = ['pivot', 'table', 'bar', 'stacked', 'line', 'donut'];
export const LIMITS = {rows: 2, cols: 1, values: 4, filters: 3};
const COUNT = '__count';
export const empty = (visual = 'pivot') => ({visual, rows: [], cols: [], values: [], filters: [], title: ''});

// The fields pane: the grains of the default date, then the shown dimensions; a record count, then the shown
// measures. all: every field of the screen, hidden ones too, for the full list.
export function fieldsOf(dataset, lang) {
  const own = kind => dataset.fields.filter(x => x.kind === kind && !x.hidden);
  const grains = dataset.default_date ? GRAINS.map(name => ({name, kind: 'dim', agg: null, grain: true, title: fieldTitle(dataset, name, lang)})) : [];
  return {
    dims: [...grains, ...own('dim')],
    measures: [{name: COUNT, kind: 'measure', agg: 'count', title: fieldTitle(dataset, COUNT, lang)}, ...own('measure')],
    all: dataset.fields,
  };
}

// The server groups by a dimension field or a grain, filters a dimension field only, and sums a measure.
export function accepts(field, well) {
  if (field.kind === 'measure') return well === 'values';
  return field.kind === 'dim' && ['rows', 'cols', 'filters'].includes(well) && !(well === 'filters' && GRAINS.includes(field.name));
}

const key = item => (typeof item === 'string' ? item : item.field);
export const wellOf = (state, name) => Object.keys(LIMITS).find(well => state[well].some(item => key(item) === name)) || null;

export function remove(state, field) {
  const name = typeof field === 'string' ? field : field.name;
  const well = wellOf(state, name);
  return well ? {...state, [well]: state[well].filter(item => key(item) !== name)} : state;
}

// A refused placement returns the same state, so the page can tell. A full well lets its oldest go, as in the comp.
export function place(state, field, well) {
  if (!LIMITS[well] || !accepts(field, well) || wellOf(state, field.name) === well) return state;
  const next = remove(state, field);
  // a measure with no default aggregation is summed (the server asks for {field, agg})
  const item = well === 'values' ? (field.agg ? field.name : {field: field.name, agg: 'sum'})
    : well === 'filters' ? {field: field.name, value: null} : field.name;
  return {...next, [well]: [...next[well], item].slice(-LIMITS[well])};
}

export const setFilter = (state, name, value) => ({...state, filters: state.filters.map(x => (x.field === name ? {...x, value} : x))});

// The ReportPanel a state makes, or null while it cannot run: a pivot needs a row, a chart an axis, all a value.
// Chart: x = rows[0], series = the column or else rows[1]. A filter without a value is not sent.
export function toPanel(state, dataset) {
  if (!state.values.length) return null;
  const filters = Object.fromEntries(state.filters.filter(x => x.value !== null).map(x => [x.field, x.value]));
  const own = {filters, ...(state.title ? {title: state.title} : {})};
  if (state.visual === 'pivot') {
    return state.rows.length ? {kind: 'pivot', dataset: dataset.key, rows: state.rows, ...(state.cols.length ? {cols: state.cols[0]} : {}), value: state.values[0], ...own} : null;
  }
  if (state.visual === 'table') return {kind: 'table', dataset: dataset.key, rows: state.rows, values: state.values, ...own};
  if (!state.rows.length) return null;
  const series = state.cols[0] ?? state.rows[1];
  return {kind: 'chart', dataset: dataset.key, mark: state.visual, x: state.rows[0], y: state.values, ...(series ? {series} : {}), ...own};
}

// A saved panel back into wells: a chart's series goes to columns. A KPI opens as a one-value table.
export function fromPanel(panel) {
  const filters = Object.entries(panel.filters || {}).map(([field, value]) => ({field, value}));
  const base = {filters, title: panel.title || ''};
  if (panel.kind === 'pivot') return {visual: 'pivot', rows: [...panel.rows], cols: panel.cols ? [panel.cols] : [], values: [panel.value], ...base};
  if (panel.kind === 'table') return {visual: 'table', rows: [...panel.rows], cols: [], values: [...panel.values], ...base};
  if (panel.kind === 'chart') return {visual: panel.mark, rows: [panel.x], cols: panel.series ? [panel.series] : [], values: [...panel.y], ...base};
  return {visual: 'table', rows: [], cols: [], values: [panel.measure], ...base, filters: []};
}

// The name a panel gets when the person types none: its measures by its dimensions.
export function autoTitle(state, dataset, lang) {
  const he = lang === 'he', t = name => fieldTitle(dataset, name, lang);
  if (!state.values.length) return dataset.title || dataset.key;
  const m = state.values.map(v => t(nameOf(v))).join(', ');
  const d = [...state.rows, ...state.cols].map(t);
  return d.length ? `${m} ${he ? 'לפי' : 'by'} ${d.join(he ? ' ו' : ' and ')}` : m;
}

// What the chosen visual type leaves out, by well, so the page can say so: a pivot draws one value, a table has
// no columns, a chart with a column draws one row.
export function unused(state) {
  const out = {};
  if (state.visual === 'pivot' && state.values.length > 1) out.values = state.values.slice(1).map(nameOf);
  if (state.visual === 'table' && state.cols.length) out.cols = [...state.cols];
  if (!['pivot', 'table'].includes(state.visual) && state.cols.length && state.rows.length > 1) out.rows = state.rows.slice(1);
  if (!['pivot', 'table'].includes(state.visual) && state.cols.length && state.values.length > 1) out.values = state.values.slice(1).map(nameOf);
  return out;
}

const HEAD = {rows: 'rows', x: 'rows', cols: 'cols', series: 'cols', value: 'values', values: 'values', y: 'values', filters: 'filters'};
const WELL_WORDS = {rows: ['בשורות', 'in rows'], cols: ['בעמודות', 'in columns'], values: ['בערכים', 'in values'], filters: ['במסננים', 'in filters']};
// The preview route's refusals (app/routers/bi.py) come without a detail wrapper: the adapter keeps their JSON as text.
const bodyOf = detail => {
  if (typeof detail !== 'string') return detail ?? null;
  try { return JSON.parse(detail); } catch { return detail; }
};

// A preview failure -> {state, text, note, well?, reason?, retry?}. A field_path names the well that holds the
// field (by the panel's own value at that path), else the path's own word; a path no well holds stays on the preview.
export function previewError(error, state, panel, lang) {
  const he = lang === 'he', w = (a, b) => (he ? a : b);
  const status = error?.status, body = bodyOf(error?.detail);
  if (status === 422 && body?.field_path) {
    const m = /^panels\[\d+\]\.(\w+)(?:\[(\d+)\])?(?:\.(.+))?$/.exec(body.field_path);
    const path = m ? body.field_path.replace(/^panels\[\d+\]\./, '') : body.field_path;
    let value = m && panel ? panel[m[1]] : null;
    if (m && m[2] !== undefined && Array.isArray(value)) value = value[Number(m[2])];
    const name = m?.[1] === 'filters' ? m[3] : typeof value === 'string' ? value : value?.field ?? null;
    const well = (name && wellOf(state, name)) || (m && HEAD[m[1]]) || null;
    return well
      ? {state: 'refused', well, reason: body.reason, text: w(`השרת סירב לשדה ${WELL_WORDS[well][0]}.`, `The server refused the field ${WELL_WORDS[well][1]}.`), note: `${path}: ${body.reason}`}
      : {state: 'refused', well: null, reason: body.reason, text: w('השרת סירב להגדרה הזאת.', 'The server refused this definition.'), note: `${path}: ${body.reason}`};
  }
  if (status === 422 && body?.code === 'cost') {
    const cost = qty(Math.round(body.cost)), ceiling = qty(Math.round(body.ceiling));
    return {state: 'refused', note: body.message ?? null, text: w(
      `השאילתה כבדה מדי לתצוגה: עלות משוערת ${cost}, מעל התקרה ${ceiling}. הוסיפו מסנן או צמצמו את השדות.`,
      `This query is too heavy to preview: estimated cost ${cost}, over the ceiling of ${ceiling}. Add a filter or use fewer fields.`)};
  }
  if (status === 422) return {state: 'refused', text: w('השרת סירב לתצוגה הזאת.', 'The server refused this preview.'), note: typeof body === 'string' ? body : null};
  if (status === 404) return {state: 'unavailable', text: w('מקור הנתונים אינו זמין.', 'This data source is unavailable.'), note: typeof body === 'string' ? body : null};
  if (status === 503 && body?.code === 'slow') {
    return {state: 'failed', retry: true, text: w('התצוגה ארכה יותר מדי. נסו שוב, או צמצמו אותה במסנן.', 'The preview took too long. Try again, or narrow it with a filter.'), note: null};
  }
  if (status === 429) {
    const s = error.retryAfter;
    return {state: 'failed', retry: true, note: null, text: s
      ? w(`יותר מדי תצוגות בבת אחת. נסו שוב בעוד ${s} שניות.`, `Too many previews at once. Try again in ${s} seconds.`)
      : w('יותר מדי תצוגות בבת אחת. נסו שוב בעוד רגע.', 'Too many previews at once. Try again in a moment.')};
  }
  return {state: 'failed', retry: true, text: w('לא ניתן לקרוא את התצוגה. נסו שוב.', 'Could not read the preview. Try again.'), note: null};
}

// A refused Save in the page's language; the server's own reason is kept, as it names what to fix.
export function saveText(error, lang) {
  const he = lang === 'he', w = (a, b) => (he ? a : b);
  const status = error?.status, body = bodyOf(error?.detail);
  if (status === 409) return w('הלוח השתנה ונטען מחדש. שמרו שוב.', 'The board changed and was reloaded. Save again.');
  if (status === 422 && body === 'panel limit reached') return w('הלוח מלא (12 פריטים). בחרו לוח אחר.', 'This board is full (12 panels). Choose another.');
  const reason = body?.reason ?? (typeof body === 'string' ? body : null);
  if (status === 422) return reason ? w(`השרת סירב לשמור: ${reason}`, `The server refused to save: ${reason}`) : w('השרת סירב לשמור.', 'The server refused to save.');
  if (status === 429 && error.retryAfter) return w(`יותר מדי בקשות. נסו שוב בעוד ${error.retryAfter} שניות.`, `Too many requests. Try again in ${error.retryAfter} seconds.`);
  return w('לא ניתן לשמור. נסו שוב.', 'Could not save. Try again.');
}
